import React, { useEffect, useState } from "react";
import {
	Button,
	Checkbox,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	List,
	ListItemButton,
	ListItemText,
	Stack,
	Typography
} from "@mui/material";
import { Facebook, Instagram } from "@mui/icons-material";
import { toast } from "react-toastify";
import api from "../../services/api";

// Conectar Facebook Messenger / Instagram Direct pelo app SabeZap (11/10/2026).
// O login do Facebook roda aqui; o token curto vai para o backend, que guarda o
// token de Página. Nenhum token fica no navegador.

const ESCOPOS = [
	"pages_show_list",
	"pages_messaging",
	"pages_manage_metadata",
	"business_management",
	"instagram_basic",
	"instagram_manage_messages"
].join(",");

const carregaSdk = (appId, version) =>
	new Promise((resolve, reject) => {
		if (window.FB) {
			window.FB.init({ appId, xfbml: false, version });
			return resolve(window.FB);
		}
		window.fbAsyncInit = () => {
			window.FB.init({ appId, xfbml: false, version });
			resolve(window.FB);
		};
		const s = document.createElement("script");
		s.src = "https://connect.facebook.net/pt_BR/sdk.js";
		s.async = true;
		s.onerror = () => reject(new Error("Não foi possível carregar o login do Facebook"));
		document.body.appendChild(s);
	});

const erroDe = err =>
	err?.response?.data?.error || err?.message || "Falha ao falar com o Facebook";

const MetaChannelsModal = ({ open, onClose }) => {
	const [carregando, setCarregando] = useState(false);
	const [token, setToken] = useState(null);
	const [paginas, setPaginas] = useState([]);
	const [pagina, setPagina] = useState(null);
	const [canais, setCanais] = useState({ facebook: true, instagram: true });

	useEffect(() => {
		if (!open) {
			setToken(null);
			setPaginas([]);
			setPagina(null);
		}
	}, [open]);

	const entrar = async () => {
		setCarregando(true);
		try {
			const { data: cfg } = await api.get("/meta-channels/config");
			if (!cfg.appId) throw new Error("App da Meta não configurado nesta instância (META_APP_ID)");
			const FB = await carregaSdk(cfg.appId, cfg.graphVersion);
			const resp = await new Promise(resolve =>
				FB.login(resolve, { scope: ESCOPOS, return_scopes: true })
			);
			if (!resp?.authResponse?.accessToken) {
				throw new Error("Login do Facebook cancelado");
			}
			setToken(resp.authResponse.accessToken);
			const { data } = await api.post("/meta-channels/pages", {
				userAccessToken: resp.authResponse.accessToken
			});
			setPaginas(data);
			if (!data.length) toast.warn("Nenhuma Página do Facebook encontrada neste usuário.");
		} catch (err) {
			toast.error(erroDe(err));
		} finally {
			setCarregando(false);
		}
	};

	const conectar = async () => {
		const lista = Object.keys(canais).filter(c => canais[c]);
		if (!pagina || !lista.length) return;
		setCarregando(true);
		try {
			await api.post("/meta-channels/connect", {
				userAccessToken: token,
				pageId: pagina.id,
				canais: lista
			});
			toast.success("Canal conectado.");
			onClose();
		} catch (err) {
			toast.error(erroDe(err));
		} finally {
			setCarregando(false);
		}
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle>Conectar Facebook e Instagram</DialogTitle>
			<DialogContent dividers>
				{!token ? (
					<Stack spacing={2}>
						<Typography variant="body2">
							Entre com um usuário do Facebook que administra a Página da empresa. O Instagram
							precisa ser conta profissional e estar vinculado a essa Página.
						</Typography>
						<Button
							variant="contained"
							startIcon={carregando ? <CircularProgress size={18} /> : <Facebook />}
							disabled={carregando}
							onClick={entrar}
						>
							Entrar com Facebook
						</Button>
					</Stack>
				) : (
					<Stack spacing={2}>
						<Typography variant="subtitle2">Escolha a Página:</Typography>
						<List dense>
							{paginas.map(p => (
								<ListItemButton
									key={p.id}
									selected={pagina?.id === p.id}
									onClick={() => setPagina(p)}
								>
									<ListItemText
										primary={p.name}
										secondary={
											p.instagram
												? `Instagram: @${p.instagram.username}`
												: "sem Instagram profissional vinculado"
										}
									/>
								</ListItemButton>
							))}
						</List>
						{pagina && (
							<Stack direction="row" spacing={2}>
								<FormControlLabel
									control={
										<Checkbox
											checked={canais.facebook}
											onChange={e => setCanais({ ...canais, facebook: e.target.checked })}
										/>
									}
									label={<><Facebook fontSize="small" /> Messenger</>}
								/>
								<FormControlLabel
									disabled={!pagina.instagram}
									control={
										<Checkbox
											checked={!!pagina.instagram && canais.instagram}
											onChange={e => setCanais({ ...canais, instagram: e.target.checked })}
										/>
									}
									label={<><Instagram fontSize="small" /> Instagram Direct</>}
								/>
							</Stack>
						)}
					</Stack>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Cancelar</Button>
				{token && (
					<Button
						variant="contained"
						disabled={!pagina || carregando}
						onClick={conectar}
					>
						{carregando ? <CircularProgress size={18} /> : "Conectar"}
					</Button>
				)}
			</DialogActions>
		</Dialog>
	);
};

export default MetaChannelsModal;
